import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCurrencyById,
  getCurrencyDenominations,
  createCurrencyDenomination,
  updateCurrencyDenomination,
  deleteCurrencyDenomination,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// Manages one currency's note/coin values — the only thing these feed is
// the Checkout screen's quick-cash pad (see Checkout.js), where tapping a
// value adds it onto a cash payment's received amount so the cashier can
// register "customer handed over a 1000 note" with one tap instead of
// typing it, and immediately sees the change owed. Kept as its own screen
// (reached from the Currencies list) rather than a field on the currency
// form itself, since a currency can have any number of these.
export default function CurrencyDenominations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("currencies");
  const { t: tCommon } = useTranslation("common");

  const [currency, setCurrency] = useState(null);
  const [denominations, setDenominations] = useState([]);

  const [newValue, setNewValue] = useState("");
  const [newSortOrder, setNewSortOrder] = useState("");
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSortOrder, setEditSortOrder] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [deleteStatus, setDeleteStatus] = useState("idle");

  const fetchAll = async () => {
    try {
      const [c, list] = await Promise.all([
        getCurrencyById(token, id),
        getCurrencyDenominations(token, id),
      ]);
      setCurrency(c);
      setDenominations(list || []);
    } catch (err) {
      notify.error(t("notify_load_denominations_failed", "Failed to load this currency's denominations"));
    }
  };

  useEffect(() => { fetchAll(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!newValue || Number(newValue) <= 0) {
      notify.error(t("validation_denomination_value", "Enter a denomination value greater than zero"));
      return;
    }
    setAdding(true);
    try {
      await createCurrencyDenomination(token, id, {
        value: Number(newValue),
        sort_order: newSortOrder === "" ? 0 : Number(newSortOrder),
      });
      notify.success(t("notify_denomination_added", "Denomination added"));
      setNewValue("");
      setNewSortOrder("");
      fetchAll();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_denomination_add_failed", "Failed to add denomination"));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditValue(String(d.value));
    setEditSortOrder(String(d.sort_order ?? 0));
  };
  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (denomId) => {
    if (!editValue || Number(editValue) <= 0) {
      notify.error(t("validation_denomination_value", "Enter a denomination value greater than zero"));
      return;
    }
    setSavingEdit(true);
    try {
      await updateCurrencyDenomination(token, denomId, {
        value: Number(editValue),
        sort_order: editSortOrder === "" ? 0 : Number(editSortOrder),
      });
      notify.success(t("notify_denomination_updated", "Denomination updated"));
      setEditingId(null);
      fetchAll();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_denomination_update_failed", "Failed to update denomination"));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteClick = (denomId) => setModalConfig({ isOpen: true, id: denomId });

  const confirmDelete = async () => {
    try {
      setDeleteStatus("loading");
      await deleteCurrencyDenomination(token, modalConfig.id);
      setDeleteStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setDeleteStatus("idle");
      }, 1000);
      notify.success(t("notify_denomination_deleted", "Denomination deleted"));
      fetchAll();
    } catch (err) {
      setDeleteStatus("error");
      notify.error(err.data?.error || err.message || t("notify_denomination_delete_failed", "Failed to delete denomination"));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_denomination_confirm_title", "Delete Denomination")}
        message={t("delete_denomination_confirm_message", "Are you sure you want to remove this denomination?")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={deleteStatus === "loading"}
        isSuccess={deleteStatus === "success"}
        isError={deleteStatus === "error"}
      />

      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/currencies")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">
            {currency ? t("denominations_title", "{name} Denominations").replace("{name}", currency.name) : t("denominations_title_fallback", "Denominations")}
          </h2>
          <p className="text-gray-400 text-sm italic font-medium">
            {t("denominations_subtitle", "The note/coin values that show up as quick-tap buttons on the Checkout cash pad")}
          </p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-6">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <FloatingInput label={t("field_value_label", "Value")} type="number" value={newValue} onChange={(e) => setNewValue(e.target.value)} />
          </div>
          <div className="w-32">
            <FloatingInput label={t("field_order_label", "Order")} type="number" value={newSortOrder} onChange={(e) => setNewSortOrder(e.target.value)} />
          </div>
          <Button variant="primary" icon={ICONS.Plus} onClick={handleAdd} loading={adding}>{tCommon("btn_add", "Add")}</Button>
        </div>

        {denominations.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-8">
            {t("denominations_empty", "No denominations yet — add this currency's note/coin values above.")}
          </p>
        ) : (
          <div className="space-y-2">
            {denominations.map((d) => (
              <div key={d.id} className="flex items-center gap-3 border border-gray-100 rounded-2xl p-3">
                {editingId === d.id ? (
                  <>
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none"
                    />
                    <input
                      type="number"
                      value={editSortOrder}
                      onChange={(e) => setEditSortOrder(e.target.value)}
                      placeholder={t("field_order_label", "Order")}
                      className="w-24 text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none"
                    />
                    <Button variant="primary" size="sm" onClick={() => saveEdit(d.id)} loading={savingEdit}>{tCommon("btn_save", "Save")}</Button>
                    <Button variant="secondary" size="sm" onClick={cancelEdit}>{tCommon("btn_cancel", "Cancel")}</Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 font-mono font-bold text-gray-800">
                      {currency?.symbol || currency?.code} {Number(d.value).toLocaleString()}
                    </div>
                    <span className="text-[10px] text-gray-400 font-mono">{t("denomination_order_label", "order {n}").replace("{n}", d.sort_order)}</span>
                    <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => startEdit(d)} />
                    <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(d.id)} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
